/**
 * One person, all of it: the person page's tabs.  Members register «pro» (meeting notes 2026-10-31, spec 04).
 *
 *   Oversikt       as before (status, membership, household, app accounts, consent) + gender and stikkord
 *   Gaver          gifts by member id or by the person's own linked accounts, and cash gifts tied to them (donations.read)
 *   Arrangementer  answers given from the person's own linked accounts — never a name matched to a list (events.attendees.read)
 *   Butikk         app orders by the person's own linked accounts (store.manage, the shop on)
 *   Kontakt        the touch log («Logg kontakt»; only with «Kontaktlogg» on) and «Planlegg besøk» (a task; `tasks` on)
 *
 * A tab this person may not use is absent; the server checks each again. Every look at a tab is audited by the server
 * with the tab's name, never an amount. Results are toasts.
 */
import React, { useState } from 'react';
import { can, errText, patch, post, send, type Body } from '../lib/api';
import { useLoad, useMsg } from '../lib/hooks';
import { kr, longDate, osloTime, osloWallNow, osloWallToIso } from '../lib/format';
import { useNav } from '../lib/nav';
import { Body as CardBody, Btn, Card, Field, LoadError, Loading, Tag, TwoStep } from '../components/ui';
import { FormDialog } from '../components/dialog';
import { tabsFor, type Tab } from '../lib/member-register';

const TAB_NO: Record<Tab, string> = { overview: 'Oversikt', gifts: 'Gaver', events: 'Arrangementer', orders: 'Butikk', contact: 'Kontakt' };
const RESPONSE_NO: Record<string, string> = { COMING: 'Kommer', NOT_COMING: 'Kommer ikke', MAYBE: 'Kanskje' };
const GENDER_NO: Record<string, string> = { female: 'Kvinne', male: 'Mann' };

export { tabsFor };

export function PersonTabs({ id, overview }: { id: string; overview: React.ReactNode }) {
  const { caps } = useNav();
  const tabs = tabsFor(caps, can);
  const [tab, setTab] = useState<Tab>('overview');
  const shown = tabs.includes(tab) ? tab : 'overview';
  return <>
    {tabs.length > 1 && <div className="bnr-seg" role="group" aria-label="Om personen">
      {tabs.map((t) => <button key={t} type="button" aria-pressed={shown === t} onClick={() => setTab(t)}>{TAB_NO[t]}</button>)}
    </div>}
    {shown === 'overview' && overview}
    {shown === 'gifts' && <Gifts id={id} />}
    {shown === 'events' && <Events id={id} />}
    {shown === 'orders' && <Orders id={id} />}
    {shown === 'contact' && <Contact id={id} logOn={Boolean(caps && caps.includes('contact_log'))} tasksOn={Boolean(caps && caps.includes('tasks')) && can('tasks.manage') && can('members.link')} />}
  </>;
}

/** Gender and stikkord, on «Oversikt»: edited inline by members.register, read by everyone else who sees the page. */
export function ProfileFields({ id, profile, onSaved }: { id: string; profile: Body | null | undefined; onSaved: () => void }) {
  const p = profile || { gender: null, tags: [] };
  const [gender, setGender] = useState<string>(p.gender || '');
  const [tags, setTags] = useState<string>((p.tags || []).join(', '));
  const [busy, setBusy] = useState(false);
  const [, say] = useMsg();
  if (!can('members.register')) {
    return <p className="note" style={{ margin: 0 }}>Kjønn: {GENDER_NO[p.gender] || 'ikke oppgitt'}{(p.tags || []).length ? <> · Stikkord: {(p.tags as string[]).map((t) => <Tag key={t} tone="grey">{t}</Tag>)}</> : null}</p>;
  }
  const dirty = gender !== (p.gender || '') || tags !== (p.tags || []).join(', ');
  const save = async () => {
    if (busy) return;
    setBusy(true);
    const r = await patch(`/members/${encodeURIComponent(id)}/profile`, { gender: gender || null, tags }).finally(() => setBusy(false));
    if (r.ok) { setTags((r.body.tags || []).join(', ')); say('Lagret.', 'ok'); onSaved(); } else say(errText(r.body), 'err');
  };
  return <div className="mreg-filters" style={{ marginTop: 12 }}>
    <Field label="Kjønn (valgfritt)" style={{ margin: 0 }} note="Bare når personen har oppgitt det.">{(fid, d) => <select id={fid} aria-describedby={d} value={gender} onChange={(e) => setGender(e.target.value)}>
      <option value="">Ikke oppgitt</option><option value="female">Kvinne</option><option value="male">Mann</option></select>}</Field>
    <Field label="Stikkord" style={{ flex: '1 1 240px', margin: 0 }} note="Skilt med komma, for eksempel «frivillig, koran». Maks 200 tegn.">{(fid, d) =>
      <input id={fid} aria-describedby={d} maxLength={200} value={tags} onChange={(e) => setTags(e.target.value)} />}</Field>
    <div className="acts" style={{ margin: 0 }}><Btn disabled={!dirty || busy} aria-busy={busy || undefined} onClick={save}>{busy ? 'Lagrer …' : 'Lagre'}</Btn></div>
  </div>;
}

function Gifts({ id }: { id: string }) {
  const { data, error, reload } = useLoad<Body>(`/members/${encodeURIComponent(id)}/gifts`, [id]);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const items: Body[] = data.items || [];
  return <Card title="Gaver" hint="Gaver gitt med medlemsnummeret, fra personens egen app-konto, og kontantgaver knyttet til personen.">
    <CardBody><div className="mperson-facts"><span>I år ({data.totals.year}): <b>{kr(data.totals.thisYear)}</b></span><span>Totalt: <b>{kr(data.totals.allTime)}</b></span></div></CardBody>
    {items.length ? <table className="t"><thead><tr><th scope="col">Dato</th><th scope="col">Formål</th><th scope="col">Måte</th><th scope="col" className="num">Beløp</th></tr></thead>
      <tbody>{items.map((g, i) => <tr key={`${g.source}-${g.date}-${g.receiptNo || ''}-${i}`}><td>{g.date ? longDate(g.date) : '—'}</td><td>{g.purpose_no}{g.correction ? <> <Tag tone="grey">korrigering</Tag></> : null}</td>
        <td>{g.method_no}{g.receiptNo ? ` · ${g.receiptNo}` : ''}</td><td className="num">{kr(g.amount)}</td></tr>)}</tbody></table>
      : <CardBody><p className="note" style={{ margin: 0 }}>Ingen gaver knyttet til personen.</p></CardBody>}
  </Card>;
}

// A ticket order's state in words (Arrangementer pro, 2026-10-31): only orders that count are listed.
const TICKET_NO: Record<string, string> = { paid: 'betalt', fulfilled: 'betalt', partly_refunded: 'delvis refundert' };

function Events({ id }: { id: string }) {
  const { data, error, reload } = useLoad<Body>(`/members/${encodeURIComponent(id)}/events`, [id]);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const items: Body[] = data.items || [], tickets: Body[] = data.tickets || [];
  return <Card title="Arrangementer" hint="Svar gitt fra personens egen app-konto. Påmeldinger skrevet inn med navn kobles ikke hit.">
    {!data.linkedAccounts ? <CardBody><p className="note" style={{ margin: 0 }}>Personen har ingen koblet app-konto, så det finnes ingen svar å vise.</p></CardBody>
      : !items.length && !tickets.length ? <CardBody><p className="note" style={{ margin: 0 }}>Ingen svar ennå.</p></CardBody>
      : <table className="t"><thead><tr><th scope="col">Arrangement</th><th scope="col">Dato</th><th scope="col">Svar</th></tr></thead>
        <tbody>{items.map((r) => <tr key={r.eventId}><td>{r.title}</td><td>{r.startsAt ? osloTime(r.startsAt) : '—'}</td>
          <td>{RESPONSE_NO[r.response] || r.response}{r.partySize > 1 ? ` · ${r.partySize} personer` : ''}</td></tr>)}
          {tickets.map((t, i) => <tr key={'t' + i}><td>{t.title}</td><td>{t.startsAt ? osloTime(t.startsAt) : '—'}</td>
            <td>Billett{t.quantity > 1 ? ` × ${t.quantity}` : ''}{TICKET_NO[t.status] ? ` · ${TICKET_NO[t.status]}` : ''}{t.checkedIn ? ' · sjekket inn' : ''}</td></tr>)}</tbody></table>}
  </Card>;
}

function Orders({ id }: { id: string }) {
  const { data, error, reload } = useLoad<Body>(`/members/${encodeURIComponent(id)}/orders`, [id]);
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const items: Body[] = data.items || [];
  return <Card title="Butikk" hint="Bestillinger fra personens egen app-konto.">
    {items.length ? <table className="t"><thead><tr><th scope="col">Ordre</th><th scope="col">Dato</th><th scope="col">Status</th><th scope="col" className="num">Sum</th></tr></thead>
      <tbody>{items.map((o) => <tr key={o.id}><td>{o.orderNo ? `Ordre ${o.orderNo}` : '—'}</td><td>{osloTime(o.createdAt)}</td><td>{o.status_no}</td><td className="num">{kr(o.total)}</td></tr>)}</tbody></table>
      : <CardBody><p className="note" style={{ margin: 0 }}>{data.linkedAccounts ? 'Ingen bestillinger.' : 'Personen har ingen koblet app-konto.'}</p></CardBody>}
  </Card>;
}

/** «Kontakt»: the touch log (only with «Kontaktlogg» on) and the planned visits (only with the work hub on). */
function Contact({ id, logOn, tasksOn }: { id: string; logOn: boolean; tasksOn: boolean }) {
  return <>
    {logOn && <Touches id={id} />}
    {tasksOn && <Visits id={id} />}
  </>;
}

function Touches({ id }: { id: string }) {
  const { data, error, reload } = useLoad<Body>(`/touches?kind=member&id=${encodeURIComponent(id)}`, [id]);
  const [open, setOpen] = useState(false);
  const [, say] = useMsg();
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const items: Body[] = data.items || [];
  return <Card title="Kontaktlogg" hint="Når moskeen ringte, skrev til, besøkte eller møtte personen. Bare for ansatte.">
    {data.can && data.can.log && <CardBody><Btn onClick={() => setOpen(true)}>Logg kontakt</Btn></CardBody>}
    {items.length ? <table className="t"><thead><tr><th scope="col">Når</th><th scope="col">Hvordan</th><th scope="col">Notat</th><th scope="col">Av</th>
      {data.can && data.can.log && <th scope="col"><span className="vh">Handling</span></th>}</tr></thead>
      <tbody>{items.map((t) => <tr key={t.id}><td>{osloTime(t.touchedAt)}</td><td>{t.channelLabel}</td><td style={{ whiteSpace: 'pre-line' }}>{t.note || '—'}</td><td>{t.createdByName || '—'}</td>
        {data.can && data.can.log && <td><TwoStep label="Slett" confirmLabel="Klikk igjen for å slette" ariaLabel={`Slett kontakten ${osloTime(t.touchedAt)}`} onConfirm={async () => {
          const r = await send(`/touches/${encodeURIComponent(t.id)}`, { method: 'DELETE' });
          say(r.ok ? 'Slettet.' : errText(r.body), r.ok ? 'ok' : 'err'); if (r.ok) reload();
        }} /></td>}</tr>)}</tbody></table>
      : <CardBody><p className="note" style={{ margin: 0 }}>Ingen kontakt loggført ennå.</p></CardBody>}
    <LogTouch open={open} id={id} channels={data.channels || []} onClose={() => setOpen(false)} onDone={() => { setOpen(false); reload(); }} />
  </Card>;
}

function LogTouch({ open, id, channels, onClose, onDone }: { open: boolean; id: string; channels: Body[]; onClose: () => void; onDone: () => void }) {
  const [channel, setChannel] = useState('phone');
  const [note, setNote] = useState('');
  const [when, setWhen] = useState('');
  const [busy, setBusy] = useState(false);
  const [, say] = useMsg();
  const submit = async () => {
    if (busy) return;
    const touchedAt = when ? osloWallToIso(when) : null;
    if (when && !touchedAt) return say('Tidspunktet er ikke gyldig.', 'err');
    setBusy(true);
    const r = await post('/touches', { kind: 'member', id, channel, ...(note.trim() ? { note: note.trim() } : {}), ...(touchedAt ? { touchedAt } : {}) }).finally(() => setBusy(false));
    say(r.ok ? 'Kontakten er loggført.' : errText(r.body), r.ok ? 'ok' : 'err');
    if (r.ok) { setNote(''); setWhen(''); onDone(); }
  };
  return <FormDialog open={open} title="Logg kontakt" dirty={Boolean(note || when)} onClose={onClose}>
    <Field label="Hvordan" required>{(fid) => <select id={fid} value={channel} onChange={(e) => setChannel(e.target.value)}>
      {channels.map((c) => <option key={c.key} value={c.key}>{c.label_no}</option>)}</select>}</Field>
    <Field label="Når (tomt = nå)">{(fid) => <input id={fid} type="datetime-local" max={osloWallNow()} value={when} onChange={(e) => setWhen(e.target.value)} />}</Field>
    <Field label="Notat (valgfritt)" note="Kort, og bare det moskeen trenger. Maks 500 tegn.">{(fid, d) =>
      <textarea id={fid} aria-describedby={d} rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />}</Field>
    <div className="acts"><Btn disabled={busy} aria-busy={busy || undefined} onClick={submit}>{busy ? 'Lagrer …' : 'Logg kontakt'}</Btn><Btn ghost onClick={onClose}>Avbryt</Btn></div>
  </FormDialog>;
}

/** «Planlegg besøk»: a task about this person, in Oppgaver; its title names the member number, never the name. */
function Visits({ id }: { id: string }) {
  const { data, error, reload } = useLoad<Body>(`/members/${encodeURIComponent(id)}/visits`, [id]);
  const [dueOn, setDueOn] = useState('');
  const [assignee, setAssignee] = useState('');
  const [busy, setBusy] = useState(false);
  const [, say] = useMsg();
  if (error) return <LoadError error={error} retry={reload} />;
  if (!data) return <Loading />;
  const plan = async () => {
    if (busy) return;
    setBusy(true);
    const r = await post(`/members/${encodeURIComponent(id)}/visits`, { dueOn, ...(assignee ? { assigneeId: assignee } : {}) }).finally(() => setBusy(false));
    say(r.ok ? 'Besøket er lagt inn som en oppgave under Oppgaver.' : errText(r.body), r.ok ? 'ok' : 'err');
    if (r.ok) { setDueOn(''); setAssignee(''); reload(); }
  };
  const items: Body[] = data.items || [];
  return <Card title="Besøk" hint="Et planlagt besøk blir en oppgave under Oppgaver, med medlemsnummeret — aldri navnet — i tittelen.">
    <CardBody>
      <div className="mreg-filters">
        <Field label="Dato" required style={{ margin: 0 }}>{(fid) => <input id={fid} type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />}</Field>
        <Field label="Ansvarlig (valgfritt)" style={{ margin: 0 }}>{(fid) => <select id={fid} value={assignee} onChange={(e) => setAssignee(e.target.value)}>
          <option value="">Ingen ennå</option>{(data.staff || []).map((s: Body) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>}</Field>
        <div className="acts" style={{ margin: 0 }}><Btn disabled={!dueOn || busy} aria-busy={busy || undefined} onClick={plan}>{busy ? 'Lagrer …' : 'Planlegg besøk'}</Btn></div>
      </div>
    </CardBody>
    {items.length > 0 && <table className="t"><thead><tr><th scope="col">Dato</th><th scope="col">Status</th><th scope="col">Ansvarlig</th></tr></thead>
      <tbody>{items.map((v) => <tr key={v.id}><td><a href="#/oppgaver">{v.dueOn ? longDate(v.dueOn) : '—'}</a></td><td>{v.status_no}</td><td>{v.assigneeName || '—'}</td></tr>)}</tbody></table>}
  </Card>;
}
